import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import AutocompleteInput from '@/components/base/AutocompleteInput';
import ItemEntryTable from '@/pages/billing/components/ItemEntryTable';
import PrintQuotation from '@/components/print/PrintQuotation';
import { useToast } from '@/contexts/ToastContext';
import { emptyLineItem, calcTotals, type InvoiceLineItem } from '@/types/billing';
import { mockParties, mockWarehouses, type CustomerOption } from '@/mocks/masters';
import { type MockQuotation } from '@/mocks/billing';
import { quotationService } from '@/services/quotationService';
import { getAllWarehouses, type WarehouseResponse } from '@/api/warehouse.api';
import { getCustomers } from '@/api/party.api';
import { isSameStateCheck, round2 } from '@/utils/gst';
import { formatINR } from '@/utils/format';
import { useWarehouseStore } from '@/stores/warehouseStore';
import DocumentNumberInput from '@/components/common/DocumentNumberInput';
import { getNextDocumentNumber } from '@/services/documentNumberService';
import { carryLineUnit } from '@/hooks/useUnits';

const COMPANY_STATE = '27';

const PAYMENT_TERMS_SUGGESTIONS = ['30 days net', '15 days net', 'Advance', 'COD', '45 days net', '7 days net'];
const DELIVERY_TERMS_SUGGESTIONS = ['Ex-Works', 'FOR Destination', 'CIF', 'FOB', 'DAP'];

export default function QuotationNewPage() {
  const navigate = useNavigate();
  const loc = useLocation();
  const toast = useToast();
  const { selectedWarehouseId: topWarehouseId, selectedWarehouseName: topWarehouseName } = useWarehouseStore();

  const searchParams = useMemo(() => new URLSearchParams(loc.search), [loc.search]);
  const editId = searchParams.get('edit') || (loc.state as any)?.editId || null;

  const [quotationNumber, setQuotationNumber] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [validityDays, setValidityDays] = useState(30);
  const [validUntil, setValidUntil] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().split('T')[0];
  });

  const [warehouses, setWarehouses] = useState<{ id: string; name: string }[]>([]);
  const [warehouseId, setWarehouseId] = useState(topWarehouseId || '');
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerOption | null>(null);
  const [customerQuery, setCustomerQuery] = useState('');

  const [paymentTerms, setPaymentTerms] = useState('');
  const [deliveryTerms, setDeliveryTerms] = useState('');
  const [notes, setNotes] = useState('');
  const [termsAndConditions, setTermsAndConditions] = useState(
    '1. Prices are valid for 30 days\n2. GST extra as applicable\n3. Delivery subject to stock availability'
  );
  const [showPaymentSuggestions, setShowPaymentSuggestions] = useState(false);
  const [showDeliverySuggestions, setShowDeliverySuggestions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [savedQuotation, setSavedQuotation] = useState<MockQuotation | null>(null);

  const [items, setItems] = useState<InvoiceLineItem[]>([emptyLineItem()]);

  // Fetch next quotation number dynamically based on warehouse prefix when creating new
  useEffect(() => {
    if (editId) return;
    async function fetchNextNumber() {
      try {
        const res = await getNextDocumentNumber('QUOTATION', warehouseId);
        if (res?.data?.nextNumber) {
          setQuotationNumber(res.data.nextNumber);
        }
      } catch (err) {
        console.warn('Failed to fetch next quotation number:', err);
      }
    }
    fetchNextNumber();
  }, [warehouseId, editId]);

  // Load Warehouses dynamically
  useEffect(() => {
    async function loadWarehouses() {
      try {
        const res = await getAllWarehouses();
        if (res && res.data && Array.isArray(res.data) && res.data.length > 0) {
          const list = res.data.map((w: WarehouseResponse) => ({ id: w.id, name: w.name }));
          setWarehouses(list);
          if (topWarehouseId && list.some((w) => w.id === topWarehouseId)) {
            setWarehouseId(topWarehouseId);
          } else {
            setWarehouseId(list[0].id);
          }
        } else {
          setWarehouses(mockWarehouses.map((w) => ({ id: w.id, name: w.name })));
          setWarehouseId(topWarehouseId || mockWarehouses[0]?.id || 'wh-001');
        }
      } catch (err) {
        console.warn('Failed to fetch warehouses, using fallback:', err);
        setWarehouses(mockWarehouses.map((w) => ({ id: w.id, name: w.name })));
        setWarehouseId(topWarehouseId || mockWarehouses[0]?.id || 'wh-001');
      }
    }
    loadWarehouses();
  }, [topWarehouseId]);

  // Sync when top-level warehouse selector in header changes
  useEffect(() => {
    if (!editId && topWarehouseId) {
      setWarehouseId(topWarehouseId);
      setSelectedCustomer(null);
      setCustomerQuery('');
    }
  }, [topWarehouseId, editId]);

  const handleWarehouseChange = (newWhId: string) => {
    setWarehouseId(newWhId);
    setSelectedCustomer(null);
    setCustomerQuery('');
  };

  // Load Quotation for editing if editId is provided
  useEffect(() => {
    if (!editId) return;
    async function loadQuotationForEdit() {
      try {
        const qt: any = await quotationService.getQuotation(editId);
        if (!qt) return;
        if (qt.quotationNumber || qt.quotation_number) setQuotationNumber(qt.quotationNumber || qt.quotation_number);
        if (qt.date) setDate(qt.date);
        if (qt.validityDays) setValidityDays(qt.validityDays);
        if (qt.validUntil) setValidUntil(qt.validUntil);
        if (qt.warehouseId) setWarehouseId(qt.warehouseId);
        if (qt.customerId) {
          setSelectedCustomer({
            id: qt.customerId,
            name: qt.customerName || '',
            type: 'CUSTOMER',
            gstin: qt.customerGSTIN || null,
            phone: qt.customerPhone || null,
            billingAddress: qt.billingAddress || '',
            stateCode: '27',
            isActive: true,
          });
          setCustomerQuery(qt.customerName || '');
        }
        if (qt.paymentTerms) setPaymentTerms(qt.paymentTerms);
        if (qt.deliveryTerms) setDeliveryTerms(qt.deliveryTerms);
        if (qt.notes) setNotes(qt.notes);
        if (qt.termsAndConditions) setTermsAndConditions(qt.termsAndConditions);
        if (Array.isArray(qt.items) && qt.items.length > 0) {
          setItems(qt.items.map((i: any) => ({
            id: i.id || crypto.randomUUID(),
            itemId: i.itemId || '',
            itemCode: i.itemCode || '',
            itemName: i.itemName || '',
            hsnCode: i.hsnCode || '',
            // As entered when another unit than the stock unit was used
            ...carryLineUnit({ qty: i.qty || 1, rate: i.rate || 0, enteredQty: i.enteredQty, enteredUnitId: i.enteredUnitId, enteredRate: i.enteredRate }),
            unit: i.unit || 'Pcs',
            unitId: '',
            discount: i.discountPercent || i.discount || 0,
            taxRate: i.taxRate || 18,
            taxableAmount: i.taxableAmount || 0,
            cgst: i.cgst || 0,
            sgst: i.sgst || 0,
            igst: i.igst || 0,
            total: i.totalAmount || i.total || 0,
            categoryId: '',
          })));
        }
      } catch (err) {
        console.warn('Failed to fetch quotation for editing:', err);
      }
    }
    loadQuotationForEdit();
  }, [editId]);

  const totals = useMemo(() => calcTotals(items), [items]);
  const isSameState = isSameStateCheck(selectedCustomer?.stateCode ?? '', COMPANY_STATE);

  // GST totals
  const cgstTotal = items.reduce(
    (s, i) => s + (isSameState ? round2((i.qty * i.rate * (1 - i.discount / 100)) * (i.taxRate / 200)) : 0),
    0
  );
  const sgstTotal = items.reduce(
    (s, i) => s + (isSameState ? round2((i.qty * i.rate * (1 - i.discount / 100)) * (i.taxRate / 200)) : 0),
    0
  );
  const igstTotal = items.reduce(
    (s, i) => s + (!isSameState ? round2((i.qty * i.rate * (1 - i.discount / 100)) * (i.taxRate / 100)) : 0),
    0
  );
  const taxTotal = cgstTotal + sgstTotal + igstTotal;
  const discountTotal = totals.subtotal - totals.taxableAmount;
  const grandTotal = round2(totals.taxableAmount + taxTotal);

  // Fetch customers strictly based on selected warehouse
  const fetchParties = useCallback(
    async (q: string): Promise<CustomerOption[]> => {
      try {
        const res = await getCustomers({
          search: q,
          isActive: true,
          warehouse_id: warehouseId && warehouseId !== 'ALL' ? warehouseId : undefined,
        });
        if (res && res.data && Array.isArray(res.data)) {
          return res.data.map((p: any) => ({
            id: p.id,
            name: p.name,
            type: 'CUSTOMER',
            gstin: p.gstin || null,
            phone: p.phone || p.mobile || null,
            billingAddress: p.billing_address || p.billingAddress || '',
            stateCode: p.state_code || p.stateCode || '',
            isActive: p.is_active ?? true,
          }));
        }
      } catch (e) {
        console.warn('Customer API search failed:', e);
      }
      return [];
    },
    [warehouseId]
  );

  const handleCustomerSelect = useCallback((p: CustomerOption) => {
    setSelectedCustomer(p);
    setCustomerQuery(p.name);
  }, []);

  const handleValidityDaysChange = (days: number) => {
    setValidityDays(days);
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    setValidUntil(d.toISOString().split('T')[0]);
    setTermsAndConditions((prev) =>
      prev.replace(/Prices are valid for \d+ days/, `Prices are valid for ${days} days`)
    );
  };

  const handleValidUntilChange = (vu: string) => {
    setValidUntil(vu);
    const d1 = new Date(date);
    const d2 = new Date(vu);
    const diff = Math.round((d2.getTime() - d1.getTime()) / 86400000);
    if (diff > 0) setValidityDays(diff);
  };

  const handleDateChange = (dt: string) => {
    setDate(dt);
    const d = new Date(dt);
    d.setDate(d.getDate() + validityDays);
    setValidUntil(d.toISOString().split('T')[0]);
  };

  const validate = () => {
    if (!warehouseId) {
      toast.error('Please select a warehouse from the top right corner');
      return false;
    }
    if (!selectedCustomer) {
      toast.error('Please select a customer');
      return false;
    }
    const validItems = items.filter((i) => i.itemName.trim() && i.qty > 0 && i.rate > 0);
    if (validItems.length === 0) {
      toast.error('Please add at least one valid item with name, qty > 0, and rate > 0');
      return false;
    }
    return true;
  };

  const buildQuotationData = (status: 'DRAFT' | 'SENT'): MockQuotation => {
    const validItems = items.filter((i) => i.itemName.trim() && i.qty > 0);
    return {
      id: `qt-${Date.now()}`,
      quotationNumber: 'QT-PENDING',
      date,
      validUntil,
      validityDays,
      customerId: selectedCustomer?.id || '',
      customerName: selectedCustomer?.name || customerQuery,
      customerGSTIN: selectedCustomer?.gstin || undefined,
      warehouseId,
      status,
      subtotal: totals.subtotal,
      discountAmount: discountTotal,
      taxableAmount: totals.taxableAmount,
      cgst: cgstTotal,
      sgst: sgstTotal,
      igst: igstTotal,
      taxAmount: taxTotal,
      totalAmount: grandTotal,
      notes: notes || undefined,
      termsAndConditions: termsAndConditions || undefined,
      paymentTerms: paymentTerms || undefined,
      deliveryTerms: deliveryTerms || undefined,
      preparedBy: 'Admin User',
      createdAt: new Date().toISOString(),
      items: validItems.map((i, idx) => ({
        id: `qti-${idx + 1}`,
        itemId: i.itemId,
        itemName: i.itemName,
        hsnCode: i.hsnCode,
        qty: i.qty,
        unit: i.unit || 'Pcs',
        rate: i.rate,
        discountPercent: i.discount,
        discountAmount: round2(i.qty * i.rate * (i.discount / 100)),
        taxableAmount: i.taxableAmount,
        taxRate: i.taxRate,
        cgst: i.cgst,
        sgst: i.sgst,
        igst: i.igst,
        totalAmount: i.total,
      })),
    };
  };

  const handleSave = async (status: 'DRAFT' | 'SENT') => {
    if (!validate()) return;
    setSaving(true);
    try {
      const validItems = items.filter((i) => i.itemName.trim() && i.qty > 0);
      const payload = {
        quotationNumber,
        customerId: selectedCustomer?.id || '',
        customerName: selectedCustomer?.name || customerQuery,
        customerGSTIN: selectedCustomer?.gstin || undefined,
        customerPhone: selectedCustomer?.phone || undefined,
        warehouseId,
        date,
        validUntil,
        validityDays,
        status,
        subtotal: totals.subtotal,
        discountAmount: discountTotal,
        taxableAmount: totals.taxableAmount,
        cgst: cgstTotal,
        sgst: sgstTotal,
        igst: igstTotal,
        taxAmount: taxTotal,
        totalAmount: grandTotal,
        notes,
        termsAndConditions,
        paymentTerms,
        deliveryTerms,
        preparedBy: 'Admin User',
        items: validItems.map((i) => ({
          itemId: i.itemId,
          itemName: i.itemName,
          hsnCode: i.hsnCode,
          description: i.description,
          qty: i.qty,
          unit: i.unit,
          // qty and rate are in this unit; the server converts them to the stock unit
          enteredUnitId: i.enteredUnitId || undefined,
          rate: i.rate,
          discountPercent: i.discount,
          discountAmount: round2(i.qty * i.rate * (i.discount / 100)),
          taxableAmount: i.taxableAmount,
          taxRate: i.taxRate,
          cgst: i.cgst,
          sgst: i.sgst,
          igst: i.igst,
          totalAmount: i.total,
        })),
      };

      let result;
      if (editId) {
        result = await quotationService.updateQuotation(editId, payload);
      } else {
        result = await quotationService.createQuotation(payload);
      }
      setSavedQuotation({
        ...buildQuotationData(status),
        id: result?.id || editId || `qt-${Date.now()}`,
        quotationNumber: result?.quotationNumber || 'QT-SAVED',
      });

      if (status === 'SENT') {
        toast.success(`Quotation saved and marked as sent`);
      } else {
        toast.success(`Quotation saved as draft`);
      }
      navigate('/sales/quotations');
    } catch (err: any) {
      console.error('Save quotation error:', err);
      const rawMsg = err?.response?.data?.message || err?.message || 'Failed to save quotation';
      if (
        rawMsg.toLowerCase().includes('already exists') ||
        rawMsg.toLowerCase().includes('duplicate') ||
        rawMsg.toLowerCase().includes('unique')
      ) {
        toast.error(`Quotation number "${quotationNumber}" already exists. Please choose or try another number.`);
      } else {
        toast.error(rawMsg);
      }
    } finally {
      setSaving(false);
    }
  };

  const getPrintData = () => savedQuotation || buildQuotationData('DRAFT');

  const fl =
    'w-full h-10 px-3 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 text-slate-700';
  const lb = 'block text-xs font-semibold text-slate-500 mb-1';

  return (
    <AppLayout>
      <div className="flex flex-col h-full bg-[#f8fafc]">
        <div className="flex-1 overflow-y-auto p-6 pb-16">
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div>
              <h1 className="text-lg font-bold text-slate-800">{editId ? 'Edit Quotation' : 'New Quotation'}</h1>
              <p className="text-sm text-slate-500">
                Create a new price estimate {topWarehouseName ? `• Warehouse: ${topWarehouseName}` : ''}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleSave('DRAFT')}
                disabled={saving}
                className="flex items-center gap-1.5 h-9 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-draft-line text-xs" /> Save as Draft
              </button>
              <button
                onClick={() => handleSave('SENT')}
                disabled={saving}
                className="flex items-center gap-1.5 h-9 px-4 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 cursor-pointer whitespace-nowrap transition-colors"
              >
                {saving ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" /> Saving...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line" /> Save &amp; Send
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Form: Header fields */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4">
            <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">Quotation Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <label className={lb}>Customer <span className="text-red-500">*</span></label>
                <AutocompleteInput<CustomerOption>
                  value={customerQuery}
                  onChange={setCustomerQuery}
                  onSelect={handleCustomerSelect}
                  fetchOptions={fetchParties}
                  placeholder={warehouseId ? "Search customer for selected warehouse..." : "Please select warehouse from top right corner first..."}
                  renderOption={(p) => (
                    <div>
                      <span className="font-medium">{p.name}</span>
                      {p.gstin && <span className="text-xs text-slate-400 ml-2">{p.gstin}</span>}
                    </div>
                  )}
                  getOptionKey={(p) => p.id}
                  getOptionLabel={(p) => p.name}
                  minChars={1}
                />
              </div>

              <div>
                <label className={lb}>QT Date</label>
                <input type="date" value={date} onChange={(e) => handleDateChange(e.target.value)} className={fl} />
              </div>

              <div>
                <label className={lb}>Valid Until</label>
                <input
                  type="date"
                  value={validUntil}
                  onChange={(e) => handleValidUntilChange(e.target.value)}
                  className={fl}
                />
              </div>

              <div>
                <label className={lb}>Validity (Days)</label>
                <input
                  type="number"
                  value={validityDays}
                  onChange={(e) => handleValidityDaysChange(Number(e.target.value) || 1)}
                  min={1}
                  max={365}
                  className={fl}
                />
              </div>

              <div className="relative">
                <label className={lb}>Payment Terms</label>
                <input
                  type="text"
                  value={paymentTerms}
                  onChange={(e) => setPaymentTerms(e.target.value)}
                  onFocus={() => setShowPaymentSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowPaymentSuggestions(false), 200)}
                  className={fl}
                  placeholder="e.g. 30 days net"
                />
                {showPaymentSuggestions && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-20 py-1">
                    {PAYMENT_TERMS_SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onMouseDown={() => {
                          setPaymentTerms(s);
                          setShowPaymentSuggestions(false);
                        }}
                        className="w-full text-left px-3 py-1.5 text-xs text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="relative md:col-span-2">
                <label className={lb}>Delivery Terms</label>
                <input
                  type="text"
                  value={deliveryTerms}
                  onChange={(e) => setDeliveryTerms(e.target.value)}
                  onFocus={() => setShowDeliverySuggestions(true)}
                  onBlur={() => setTimeout(() => setShowDeliverySuggestions(false), 200)}
                  className={fl}
                  placeholder="e.g. Ex-Works"
                />
                {showDeliverySuggestions && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-20 py-1">
                    {DELIVERY_TERMS_SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onMouseDown={() => {
                          setDeliveryTerms(s);
                          setShowDeliverySuggestions(false);
                        }}
                        className="w-full text-left px-3 py-1.5 text-xs text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Customer Details Pill if selected */}
            {selectedCustomer && (
              <div className="mt-4 p-3 bg-indigo-50/50 border border-indigo-100 rounded-lg flex items-center justify-between text-xs text-slate-600">
                <div className="flex items-center gap-4 flex-wrap">
                  <span>
                    <strong>Customer:</strong> {selectedCustomer.name}
                  </span>
                  {selectedCustomer.gstin && (
                    <span>
                      <strong>GSTIN:</strong> {selectedCustomer.gstin}
                    </span>
                  )}
                  {selectedCustomer.phone && (
                    <span>
                      <strong>Phone:</strong> {selectedCustomer.phone}
                    </span>
                  )}
                  {selectedCustomer.stateCode && (
                    <span>
                      <strong>State:</strong> {selectedCustomer.stateCode} ({isSameState ? 'Intrastate - CGST+SGST' : 'Interstate - IGST'})
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedCustomer(null);
                    setCustomerQuery('');
                  }}
                  className="text-indigo-600 hover:underline cursor-pointer font-medium"
                >
                  Change
                </button>
              </div>
            )}
          </div>

          {/* Line Items Table */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4">
            <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">Line Items</h2>
            <ItemEntryTable items={items} onChange={setItems} isSameState={isSameState} warehouseId={warehouseId} />
          </div>

          {/* Totals & Terms */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            {/* Terms & Notes */}
            <div className="lg:col-span-2 space-y-4">
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <label className={lb}>Terms &amp; Conditions</label>
                <textarea
                  value={termsAndConditions}
                  onChange={(e) => setTermsAndConditions(e.target.value)}
                  rows={4}
                  className="w-full p-3 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-400 font-mono text-slate-700 resize-none"
                  placeholder="Enter quotation terms & conditions..."
                />
              </div>

              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <label className={lb}>Internal Notes / Special Instructions</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full p-3 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-400 text-slate-700 resize-none"
                  placeholder="Notes for customer or internal reference..."
                />
              </div>
            </div>

            {/* Quotation Info & Summary */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 h-fit">
              <h2 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Quotation Info &amp; Summary</h2>

              <div className="mb-3">
                <DocumentNumberInput
                  value={quotationNumber}
                  onChange={setQuotationNumber}
                  label="Quotation Number"
                />
              </div>

              <div className="flex flex-wrap items-center gap-1.5 mb-3">
                <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                  {date ? new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'}
                </span>
                <span className="text-xs px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 font-semibold border border-indigo-200">
                  VALID: {validityDays} DAYS
                </span>
              </div>

              <div className="space-y-2.5 text-sm border-t border-slate-100 pt-3">
                <div className="flex justify-between text-slate-600">
                  <span>Subtotal</span>
                  <span className="font-medium text-slate-800">{formatINR(totals.subtotal)}</span>
                </div>
                {discountTotal > 0 && (
                  <div className="flex justify-between text-slate-600">
                    <span>Total Discount</span>
                    <span className="font-medium text-red-600">-{formatINR(discountTotal)}</span>
                  </div>
                )}
                <div className="flex justify-between text-slate-600">
                  <span>Taxable Amount</span>
                  <span className="font-medium text-slate-800">{formatINR(totals.taxableAmount)}</span>
                </div>

                {isSameState ? (
                  <>
                    <div className="flex justify-between text-slate-500 text-xs">
                      <span>CGST</span>
                      <span>{formatINR(cgstTotal)}</span>
                    </div>
                    <div className="flex justify-between text-slate-500 text-xs">
                      <span>SGST</span>
                      <span>{formatINR(sgstTotal)}</span>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-between text-slate-500 text-xs">
                    <span>IGST</span>
                    <span>{formatINR(igstTotal)}</span>
                  </div>
                )}

                <div className="flex justify-between text-slate-600 pt-1 border-t border-slate-100">
                  <span>Total Tax</span>
                  <span className="font-medium text-slate-800">{formatINR(taxTotal)}</span>
                </div>

                <div className="flex justify-between text-base font-bold text-slate-900 pt-2 border-t border-slate-200">
                  <span>Grand Total</span>
                  <span className="text-indigo-600">{formatINR(grandTotal)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Print Modal */}
        {printOpen && <PrintQuotation quotation={getPrintData()} onClose={() => setPrintOpen(false)} />}
      </div>
    </AppLayout>
  );
}